import { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { getAuth } from "../../../../../../../../../redux/auth/authSlice";
import { useDivisionMileStoneDataReleased } from "../../../../../../../../../reactQuery/division/divisionMileStoneQuery";
import { useMileStonTaksDataByHomesiteId } from "../../../../../../../../../reactQuery/homeSites/milestones/mileStoneTasksQuery";
import { MilestoneTask } from "../../../../../../../../../api/homeSiteMilestones/types";
import { CombinedMilestoneTask, CombinedMilestoneData } from "../types";


export const useMilestoneData = (homeSiteId: string) => {
     const { userInfo } = useSelector(getAuth);
     const { data: divisionMileStones } = useDivisionMileStoneDataReleased(userInfo.userDivCode);
     const { data: homeSiteMilestonesTasks } = useMileStonTaksDataByHomesiteId(userInfo.userDivCode, homeSiteId);

     // Create lookup map for home site tasks
     const homeSiteTasksMap = useMemo(() => {
          if (!homeSiteMilestonesTasks?.data) return new Map<string, MilestoneTask>();

          const map = new Map<string, MilestoneTask>();
          homeSiteMilestonesTasks.data.forEach(task => {
               map.set(`${task.mileStoneName}-${task.taskName}`, task);
          });
          return map;
     }, [homeSiteMilestonesTasks?.data]);

     // Combine and organize milestone data
     const combinedMilestoneData = useMemo((): CombinedMilestoneData => {
          if (!divisionMileStones?.data) return [];

          // Group tasks by milestone name
          const milestonesMap = new Map<string, { order: number; tasks: CombinedMilestoneTask[] }>();

          divisionMileStones.data.forEach(divisionTask => {
               const { mileStoneName, mileStoneOrder } = divisionTask;
               const taskKey = `${mileStoneName}-${divisionTask.taskName}`;
               const homeSiteTask = homeSiteTasksMap.get(taskKey);

               if (!milestonesMap.has(mileStoneName)) {
                    milestonesMap.set(mileStoneName, {
                         order: mileStoneOrder,
                         tasks: []
                    });
               }

               milestonesMap.get(mileStoneName)!.tasks.push({
                    divisionTemplate: divisionTask,
                    milestoneTaskData: homeSiteTask,
                    saleDate: homeSiteTask?.saleDate || null,
                    closeOfEscrowActual: homeSiteTask?.closeOfEscrowActual || null,
                    closeOfEscrowEstimated: homeSiteTask?.closeOfEscrowEstimated || null,
               });
          });

          // Convert to array and sort
          return Array.from(milestonesMap.entries())
               .map(([milestoneName, { order, tasks }]) => ({
                    milestoneName,
                    milestoneOrder: order,
                    tasks: tasks.sort((a, b) => a.divisionTemplate.taskOrder - b.divisionTemplate.taskOrder)
               }))
               .sort((a, b) => a.milestoneOrder - b.milestoneOrder);
     }, [divisionMileStones?.data, homeSiteTasksMap]);

     // Create lookup map for tasks by milestone for O(1) access
     const tasksByMilestone = useMemo(() => {
          const map = new Map<string, CombinedMilestoneTask[]>();
          combinedMilestoneData.forEach(milestone => {
               map.set(milestone.milestoneName, milestone.tasks);
          });
          return map;
     }, [combinedMilestoneData]);

     const getTasksForMilestone = useCallback((milestoneName: string): CombinedMilestoneTask[] => {
          return tasksByMilestone.get(milestoneName) || [];
     }, [tasksByMilestone]);

     return {
          combinedMilestoneData,
          divisionMilestonesData: divisionMileStones?.data || [],
          homeSiteMilestonesTasksData: homeSiteMilestonesTasks?.data || [],
          getTasksForMilestone,
          userInfo,
     };
}; 